import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Form } from "react-bootstrap";
import PlayerNameWithFlag from "./PlayerNameWithFlag";
import {
  PlayersIcon,
  TrophyIcon,
  MatchIcon,
  ChartIcon,
  ScoreboardIcon,
  BallIcon,
  PeakIcon,
  ChampionIcon,
  ParticipationIcon
} from "../icons/StatIcons";
import { motion, type Variants } from "framer-motion";
import type { ChangeEvent, ReactNode } from "react";
import type { LadderStatsData, Player } from "../../types/domain";

const containerVariants: Variants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.05 }
  }
};

const itemVariants: Variants = {
  hidden: { opacity: 0, y: 15 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.25 } }
};

interface LadderStatsProps {
  stats: LadderStatsData | null;
  selectedYear: number | null;
  onYearChange: (year: number | null) => void;
}

interface StatCardProps {
  icon: ReactNode;
  label: ReactNode;
  value: ReactNode;
  subValue?: ReactNode;
}

interface PlayerStatCardProps {
  icon: ReactNode;
  label: ReactNode;
  player: Player | null;
  value: ReactNode;
}

function LadderStats({ stats, selectedYear, onYearChange }: LadderStatsProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const currentYear = new Date().getFullYear();
  const years: number[] = [];
  for (let year = currentYear; year >= 2025; year--) {
    years.push(year);
  }

  const handleYearChange = (e: ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value;
    onYearChange(value === '' ? null : parseInt(value));
  };

  if (!stats) {
    return <div className="ladder-stats-loading">{t("loading")}...</div>;
  }

  const StatCard = ({ icon, label, value, subValue }: StatCardProps) => (
    <motion.div className="ladder-stat-card" variants={itemVariants}>
      <div className="ladder-stat-icon">{icon}</div>
      <div className="ladder-stat-content">
        <div className="ladder-stat-value">{value}</div>
        <div className="ladder-stat-label">{label}</div>
        {subValue && <div className="ladder-stat-subvalue">{subValue}</div>}
      </div>
    </motion.div>
  );

  const PlayerStatCard = ({ icon, label, player, value }: PlayerStatCardProps) => (
    <motion.div
      className="ladder-stat-card ladder-stat-card-player"
      onClick={() => player && navigate(`/players/${player.id}`)}
      variants={itemVariants}
    >
      <div className="ladder-stat-icon">{icon}</div>
      <div className="ladder-stat-content">
        <div className="ladder-stat-label">{label}</div>
        {player ? (
          <>
            <div className="ladder-stat-player">
              <PlayerNameWithFlag player={player} notLink={true} />
            </div>
            <div className="ladder-stat-subvalue">{value}</div>
          </>
        ) : (
          <div className="ladder-stat-value">-</div>
        )}
      </div>
    </motion.div>
  );

  return (
    <div className="ladder-stats-container">
      <div className="ladder-stats-header">
        <h3>{t("ladderStatistics")}</h3>
        <Form.Select
          size="sm"
          className="ladder-stats-year-select"
          value={selectedYear || ''}
          onChange={handleYearChange}
        >
          <option value="">{t("all")}</option>
          {years.map(year => (
            <option key={year} value={year}>{year}</option>
          ))}
        </Form.Select>
      </div>

      <motion.div
        className="ladder-stats-grid"
        variants={containerVariants}
        initial="hidden"
        animate="visible"
      >
        <StatCard
          icon={<PlayersIcon size={28} color="#4CAF50" />}
          label={t("totalPlayers")}
          value={stats.totalPlayers}
        />
        <StatCard
          icon={<TrophyIcon size={28} color="#FFC107" />}
          label={t("totalTournaments")}
          value={stats.totalTournaments}
        />
        <StatCard
          icon={<MatchIcon size={28} color="#2196F3" />}
          label={t("totalMatches")}
          value={stats.totalMatches}
        />
        <StatCard
          icon={<ChartIcon size={28} color="#9C27B0" />}
          label={t("averageElo")}
          value={stats.averageElo}
        />
        <StatCard
          icon={<ScoreboardIcon size={28} color="#FF6B00" />}
          label={t("setsPlayed")}
          value={stats.totalSetsPlayed.toLocaleString()}
        />
        <StatCard
          icon={<BallIcon size={28} color="#FFFFFF" />}
          label={t("pointsPlayed")}
          value={stats.totalPointsPlayed.toLocaleString()}
        />
      </motion.div>

      <div className="ladder-stats-records">
        <h4>{t("records")}</h4>
        <motion.div
          className="ladder-stats-records-grid"
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          <PlayerStatCard
            icon={<PeakIcon size={28} color="#FFD700" />}
            label={t("highestEloEver")}
            player={stats.highestEloPlayer}
            value={`${stats.highestEloEver} ELO`}
          />
          <PlayerStatCard
            icon={<ChampionIcon size={28} color="#FFC107" />}
            label={t("mostTournamentsWon")}
            player={stats.mostTournamentsWonPlayer}
            value={`${stats.mostTournamentsWonCount} ${t("wins")}`}
          />
          <PlayerStatCard
            icon={<ParticipationIcon size={28} color="#2196F3" />}
            label={t("mostTournamentsPlayed")}
            player={stats.mostTournamentsPlayedPlayer}
            value={`${stats.mostTournamentsPlayedCount}×`}
          />
        </motion.div>
      </div>
    </div>
  );
}

export default LadderStats;
